import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
    AppointmentSlot,
    BookingEntrySource,
    ExistingRepairBookingSession,
} from '../models/repair-bookings';
import { apiCallWithAlertOnError, startSession } from '../utils/api';

type BookingEditPageState =
    | { status: 'select' }
    | { status: 'cancel' }
    | { status: 'shop-selection' }
    | { status: 'date-selection' }
    | { status: 'time-selection'; selectedDate: string; slots: AppointmentSlot[] }
    | { status: 'personal-info' }
    | { status: 'confirm' }
    | { status: 'edit-complete'; confirmationId: string }
    | { status: 'cancel-complete' }
    | { status: 'invalid' };

type BookingEditInitialState = Extract<BookingEditPageState, { status: 'select' }>;
type BookingEditCompleteState = Extract<
    BookingEditPageState,
    { status: 'edit-complete' | 'cancel-complete' }
>;
type BookingEditOtherState = Exclude<
    BookingEditPageState,
    BookingEditInitialState | BookingEditCompleteState
>;

const PAGE_CONFIG: Record<
    BookingEditPageState['status'],
    { title: string; showShopHeader: boolean }
> = {
    select: { title: '予約の変更／キャンセル', showShopHeader: false },
    cancel: { title: '予約の変更／キャンセル', showShopHeader: false },
    'shop-selection': { title: '店舗の変更', showShopHeader: false },
    'date-selection': { title: '日時の変更', showShopHeader: true },
    'time-selection': { title: '日時の変更', showShopHeader: true },
    'personal-info': { title: 'お客さま情報の編集', showShopHeader: true },
    confirm: { title: '予約内容の確認', showShopHeader: true },
    'edit-complete': { title: '完了', showShopHeader: false },
    'cancel-complete': { title: '完了', showShopHeader: false },
    invalid: { title: 'エラー', showShopHeader: false },
};

const BOOKING_EDIT_BASE_PATH_PREFIX: Record<BookingEntrySource, string> = {
    web: '/bookings',
    console: '/console/bookings',
};

const getInitialPageState = (entrySource: BookingEntrySource): BookingEditPageState =>
    entrySource === 'console' ? { status: 'shop-selection' } : { status: 'select' };

const useBookingEditPages = (token: string, entrySource: BookingEntrySource, csrfToken: string) => {
    const [pageStack, setPageStack] = useState<BookingEditPageState[]>([
        getInitialPageState(entrySource),
    ]);
    const [session, setSession] = useState<ExistingRepairBookingSession | null>(null);

    const { action, index } = useParams();
    const pageIndex = !action
        ? 0
        : action === 'cancel'
          ? index === 'complete'
              ? 0
              : 1
          : index === 'complete'
            ? 0
            : Number(index);
    const navigate = useNavigate();
    const basePath = `${BOOKING_EDIT_BASE_PATH_PREFIX[entrySource]}/${token}`;

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPageStack((stack) =>
            stack.length > pageIndex + 1 ? stack.slice(0, pageIndex + 1) : stack
        );
    }, [pageIndex, setPageStack]);

    const pushPageStack = useCallback(
        (state: BookingEditOtherState) => {
            setPageStack((stack) => stack.slice(0, pageIndex + 1).concat(state));
            if (state.status === 'cancel') {
                navigate(`${basePath}/cancel/`);
            } else {
                navigate(`${basePath}/edit/${pageIndex + 1}/`);
            }
        },
        [basePath, pageIndex, navigate, setPageStack]
    );

    const popPageStack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const complete = useCallback(
        (state: BookingEditCompleteState) => {
            setPageStack([state]);
            if (state.status === 'cancel-complete') {
                navigate(`${basePath}/cancel/complete/`);
            } else {
                navigate(`${basePath}/edit/complete/`);
            }
        },
        [basePath, navigate, setPageStack]
    );

    const resetPageStack = useCallback(() => {
        setSession(null);
        setPageStack([getInitialPageState(entrySource)]);
        navigate(`${basePath}/`);
    }, [basePath, setPageStack, navigate, entrySource]);

    const currentPage =
        pageIndex < pageStack.length ? pageStack[pageIndex]! : { status: 'invalid' as const };
    const pageConfig = PAGE_CONFIG[currentPage.status];

    useEffect(() => {
        if (currentPage.status !== 'shop-selection' || session) {
            return;
        }
        void apiCallWithAlertOnError(async () => {
            const session = await startSession(csrfToken, 'reschedule');
            setSession({
                type: 'edit',
                csrfToken,
                sessionId: session.session_id,
                bookingToken: token,
            });
        })();
    }, [currentPage.status, csrfToken, session, token]);

    return {
        currentPage,
        pageConfig,
        pushPageStack,
        popPageStack,
        complete,
        resetPageStack,
        session,
    };
};

export default useBookingEditPages;
